/**
 * VapeChamp Cookie Banner UI
 * DSGVO-compliant cookie consent with granular control
 * Note: Cookie blocker runs in <head> via snippet 'vc-cookie-blocker'
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'vc_cookie_consent';
  const RELOAD_FLAG = 'vc_cookie_reload_done';

  // Cookie service definitions (from blocker - centralized config)
  const COOKIE_SERVICES = window.VC_COOKIE_SERVICES || {
    // Fallback if blocker hasn't loaded yet (shouldn't happen)
    essential: {
      name: 'Essentiell',
      required: true,
      description: 'Notwendig für den Betrieb der Website',
      services: ['Shopify Shop-Funktionen']
    },
    marketing: {
      name: 'Marketing & Tracking',
      required: false,
      description: 'Zur Analyse und Verbesserung unserer Marketing-Maßnahmen',
      services: ['Google Analytics', 'Facebook Pixel', 'TikTok Pixel']
    }
  };

  /**
   * ============================================
   * COOKIE BANNER UI
   * ============================================
   */
  class VCCookieBanner {
    constructor() {
      this.banner = document.getElementById('vc-cookie-banner');
      if (!this.banner) return;

      this.detailsArea = document.getElementById('vc-cookie-details');
      this.categoriesContainer = document.getElementById('vc-cookie-categories');

      this.buttons = {
        acceptAll: this.banner.querySelector('[data-action="accept-all"]'),
        customize: this.banner.querySelector('[data-action="customize"]'),
        save: this.banner.querySelector('[data-action="save-selection"]'),
        back: this.banner.querySelector('[data-action="back"]')
      };

      this.init();
    }

    init() {
      const consent = this.getConsent();

      // Setup event listeners (always needed for re-opening)
      this.buttons.acceptAll?.addEventListener('click', () => this.handleAcceptAll());
      this.buttons.customize?.addEventListener('click', () => this.showCustomization());
      this.buttons.save?.addEventListener('click', () => this.handleSaveSelection());
      this.buttons.back?.addEventListener('click', () => this.hideCustomization());

      // Auto-show only if no consent exists
      if (!consent) {
        this.show();
      }

      // API is exposed by the lazy loader, not here
    }

    getConsent() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
      } catch (e) {
        return null;
      }
    }

    saveConsent(data) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        // Silent fail
      }
    }

    show() {
      if (!this.banner) return;
      this.closeOpenMenus();
      this.banner.hidden = false;
    }

    hide() {
      if (!this.banner) return;
      this.banner.hidden = true;
      this.hideCustomization();
    }

    closeOpenMenus() {
      // Only close specific theme menus, not all dialogs (could interfere with other features)
      // Target only header/account menus that we know about
      const accountMenu = document.querySelector('[popover][class*="account"]');
      const headerDrawer = document.querySelector('details[class*="menu-drawer"]');
      const searchModal = document.querySelector('dialog[id*="search"]');

      if (accountMenu && accountMenu.matches(':popover-open')) {
        accountMenu.hidePopover?.();
      }
      if (headerDrawer && headerDrawer.hasAttribute('open')) {
        headerDrawer.removeAttribute('open');
      }
      if (searchModal && searchModal.hasAttribute('open')) {
        searchModal.close();
      }
    }

    handleAcceptAll() {
      this.saveConsent({ level: 'all', services: ['essential', 'marketing'] });
      this.hide();

      // Instead of reload, dynamically enable marketing scripts
      this.enableMarketingScripts();
    }

    enableMarketingScripts() {
      // Trigger custom event that marketing scripts can listen to
      window.dispatchEvent(new CustomEvent('vcCookieConsentChanged', {
        detail: { level: 'all', services: ['essential', 'marketing'] }
      }));

      // Note: Most tracking scripts from content_for_header are already blocked
      // They need a page reload to execute.
      // Future improvement: Use Google Tag Manager dataLayer.push() instead
    }

    showCustomization() {
      this.renderCategories();
      this.detailsArea.hidden = false;
      this.banner.classList.add('vc-cookie-banner--expanded');
    }

    hideCustomization() {
      this.detailsArea.hidden = true;
      this.banner.classList.remove('vc-cookie-banner--expanded');
    }

    renderCategories() {
      if (!this.categoriesContainer) return;

      const html = Object.keys(COOKIE_SERVICES).map(key => {
        const category = COOKIE_SERVICES[key];
        const services = category.services.length > 0
          ? category.services
          : category.detectedServices;

        return `
          <div class="vc-cookie-category">
            <div class="vc-cookie-category__header">
              <input
                type="checkbox"
                class="vc-cookie-category__checkbox"
                id="cookie-${key}"
                value="${key}"
                ${category.required ? 'checked disabled' : ''}
              >
              <label for="cookie-${key}" class="vc-cookie-category__title">
                ${category.name}
                ${category.required ? '<span class="vc-cookie-category__required">(unbedingt erforderlich)</span>' : ''}
              </label>
            </div>
            <div class="vc-cookie-category__services">
              ${category.description}<br>
              ${services.map(s => `<div class="vc-cookie-category__service">• ${s}</div>`).join('')}
            </div>
          </div>
        `;
      }).join('');

      this.categoriesContainer.innerHTML = html;
    }

    handleSaveSelection() {
      const checkboxes = this.categoriesContainer.querySelectorAll('input[type="checkbox"]:checked');
      const selectedServices = Array.from(checkboxes).map(cb => cb.value);

      this.saveConsent({
        level: 'custom',
        services: selectedServices
      });

      this.hide();

      if (selectedServices.includes('marketing')) {
        this.enableMarketingScripts();
      }
    }
  }

  /**
   * ============================================
   * INITIALIZATION
   * ============================================
   */

  // Early check: If consent exists, only expose minimal API
  // (Blocker already runs in <head> and handles enforcement)
  function checkAndInit() {
    const existingConsent = localStorage.getItem(STORAGE_KEY);
    let bannerInstance = null;

    if (existingConsent) {
      // Consent exists - only expose API for account menu, don't initialize banner
      window.vcCookieBanner = {
        show: function() {
          // Lazy init only when user clicks "Cookie-Einstellungen"
          if (!bannerInstance) {
            bannerInstance = new VCCookieBanner();
          }
          bannerInstance.show();
        }
      };
    } else {
      // No consent - full initialization (show banner immediately)
      bannerInstance = new VCCookieBanner();

      // Expose API for re-opening
      window.vcCookieBanner = {
        show: function() {
          bannerInstance.show();
        }
      };
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkAndInit);
  } else {
    checkAndInit();
  }

})();
